import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { useTheme } from "../context/ThemeContext";
import { useCart } from "../context/CartContext";
import { Button, buttonVariants } from "./ui/button";
import NotificationBell from "./NotificationBell";
import SearchBar from "./SearchBar";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription, SheetClose } from "./ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { ShoppingCart, Heart, MessageCircle, Sun, Moon, Globe, Menu, User2, LayoutDashboard, Sparkles, LogOut, Package } from "lucide-react";

const LANGS = [
  { code: "en", label: "English" }, { code: "hi", label: "हिन्दी" },
  { code: "kn", label: "ಕನ್ನಡ" }, { code: "te", label: "తెలుగు" }, { code: "ta", label: "தமிழ்" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, change } = useI18n();
  const { theme, setTheme } = useTheme();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const items = [
    { to: "/marketplace", label: t("marketplace") },
    { to: "/farmers", label: t("farmers") },
    { to: "/ai-tools", label: t("ai") },
  ];
  const signOut = () => { logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center gap-2 sm:gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2" aria-label="ULUME home">
          <img src="/logo.webp" alt="ULUME.shop" width="112" height="70" className="h-12 md:h-[3.75rem] w-auto object-contain dark:bg-white dark:rounded-md dark:p-1" />
        </Link>

        <nav className="hidden xl:flex items-center gap-1 ml-4" aria-label="Primary">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} data-testid={`nav-${i.to.slice(1)}`}
              className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-md transition-colors hover:text-primary ${isActive ? "text-primary bg-accent" : ""}`}>
              {i.label}
            </NavLink>
          ))}
        </nav>

        <SearchBar className="hidden md:block flex-1 max-w-lg mx-2 lg:mx-4" />

        <div className="ml-auto md:ml-0 flex items-center gap-0.5 sm:gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="lang-toggle" aria-label="Change language" className="hidden sm:inline-flex"><Globe className="w-5 h-5" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGS.map(l => (
                <DropdownMenuItem key={l.code} onClick={() => change(l.code)}
                  data-testid={`lang-${l.code}`} className={lang === l.code ? "bg-accent font-medium" : ""}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="ghost" size="icon" data-testid="theme-toggle" aria-label="Toggle dark mode" className="hidden sm:inline-flex"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          <Link to="/cart" data-testid="nav-cart" aria-label={`Cart, ${count} items`} className={`${buttonVariants({ variant: "ghost", size: "icon" })} relative hidden md:inline-flex`}>
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-secondary text-secondary-foreground text-[10px] font-bold grid place-items-center">{count > 99 ? "99+" : count}</span>
            )}
          </Link>
          {user && (
            <>
              <NotificationBell />
              <Link to="/wishlist" data-testid="nav-wishlist" aria-label="Wishlist" className={`${buttonVariants({ variant: "ghost", size: "icon" })} hidden sm:inline-flex`}><Heart className="w-5 h-5" /></Link>
              <Link to="/chat" data-testid="nav-chat" aria-label="Messages" className={`${buttonVariants({ variant: "ghost", size: "icon" })} hidden sm:inline-flex`}><MessageCircle className="w-5 h-5" /></Link>
            </>
          )}

          {!user ? (
            <div className="hidden sm:flex items-center gap-2 ml-2">
              <Link to="/login"><Button variant="ghost" data-testid="login-btn">{t("login")}</Button></Link>
              <Link to="/signup"><Button data-testid="signup-btn" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground">{t("signup")}</Button></Link>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="user-menu" className="gap-2" aria-label="Account menu">
                  <User2 className="w-4 h-4" />
                  <span className="hidden sm:inline max-w-[110px] truncate">{user.name.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-medium truncate">{user.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account"><User2 className="w-4 h-4 mr-2" />{t("my_account")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="w-4 h-4 mr-2" />{user.role === "admin" ? "Admin panel" : t("dashboard")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/orders")}><Package className="w-4 h-4 mr-2" />{t("my_orders")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/wishlist")}><Heart className="w-4 h-4 mr-2" />{t("wishlist")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/chat")}><MessageCircle className="w-4 h-4 mr-2" />{t("chat")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/ai-tools")}><Sparkles className="w-4 h-4 mr-2" />{t("ai")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />{t("logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu" aria-label="Open menu"><Menu className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent>
              <SheetTitle className="font-display text-primary">ULUME</SheetTitle>
              <SheetDescription className="sr-only">Site navigation</SheetDescription>
              <div className="flex flex-col gap-1 mt-6">
                {items.map(i => (
                  <SheetClose asChild key={i.to}><Link to={i.to} className="px-3 py-2.5 text-base font-medium hover:bg-accent rounded-md">{i.label}</Link></SheetClose>
                ))}
                {user && [["/orders", t("my_orders")], ["/wishlist", t("wishlist")], ["/chat", t("chat")], [user.role === "admin" ? "/admin" : "/dashboard", user.role === "admin" ? "Admin panel" : t("dashboard")]].map(([to, label]) => (
                  <SheetClose asChild key={to}><Link to={to} className="px-3 py-2.5 text-base font-medium hover:bg-accent rounded-md">{label}</Link></SheetClose>
                ))}
                <div className="sm:hidden mt-4 pt-4 border-t">
                  <div className="text-xs font-medium text-muted-foreground mb-2">{t("language")}</div>
                  <div className="flex flex-wrap gap-2">
                    {LANGS.map(l => <Button key={l.code} size="sm" variant={lang === l.code ? "default" : "outline"} onClick={() => change(l.code)}>{l.label}</Button>)}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                    {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}{theme === "dark" ? t("light_mode") : t("dark_mode")}
                  </Button>
                </div>
                {!user && (
                  <div className="mt-4 flex flex-col gap-2">
                    <SheetClose asChild><Link to="/login"><Button variant="outline" className="w-full">{t("login")}</Button></Link></SheetClose>
                    <SheetClose asChild><Link to="/signup"><Button className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90">{t("signup")}</Button></Link></SheetClose>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="md:hidden px-4 pb-2.5"><SearchBar /></div>
    </header>
  );
}
